import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["value", "className", "onChange", "size", "placeholder", "kind", "iconLeft", "isDisabled", "hasError", "dropdownOnBreakpoint", "isLoading", "options", "onFocus", "onBlur", "name", "isRequired"],
  _excluded2 = ["className"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link, Flex, FlexItem, Icon, Element, OptionList, useI18n, Drawer, useBreakpoint } from '../';
import getConfig from '../../js/settings';
import { appendHtmlClass, responsivePropType, breakpointPropType, getPortalTarget, isServer, throttle, getSpaceValue, getAnimationDuration, getAnimationEasing, debounce } from '../../js/util';
import withValidation from '../Form/validation';
import '../Input/_index.scss';
import './_index.scss';
var INPUT_SIZE = getConfig('INPUT_SIZE');
var INPUT_SIZE_DEFAULT = getConfig('INPUT_SIZE_DEFAULT');
var INPUT_SIZE_PREFIX = getConfig('INPUT_SIZE_PREFIX');
var INPUT_KIND = getConfig('INPUT_KIND');
var INPUT_KIND_DEFAULT = getConfig('INPUT_KIND_DEFAULT');
var INPUT_KIND_PREFIX = getConfig('INPUT_KIND_PREFIX');
var ICON_DROPDOWN = getConfig('ICON_DROPDOWN');
var ICON_SPINNER = getConfig('ICON_SPINNER');
var SelectMemo = /*#__PURE__*/React.memo(function Select(_ref) {
  var value = _ref.value,
    className = _ref.className,
    onChange = _ref.onChange,
    size = _ref.size,
    placeholder = _ref.placeholder,
    kind = _ref.kind,
    iconLeft = _ref.iconLeft,
    isDisabled = _ref.isDisabled,
    hasError = _ref.hasError,
    dropdownOnBreakpoint = _ref.dropdownOnBreakpoint,
    isLoading = _ref.isLoading,
    options = _ref.options,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    name = _ref.name,
    isRequired = _ref.isRequired,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var ref = useRef(null);
  var _useState = useState(false),
    isFocused = _useState[0],
    setIsFocused = _useState[1];
  var _useState2 = useState(false),
    isAnimating = _useState2[0],
    setIsAnimating = _useState2[1];
  var keyThrottleMs = 500;
  var selectDelayMs = 150;
  var sizeConfig = INPUT_SIZE[size];
  var showPlaceholder = useMemo(function () {
    return typeof value !== 'string';
  }, [value]);
  var listRef = useRef(null);
  var defaultPlaceholderText = useI18n('SELECT_PLACEHOLDER');
  var placeholderText = placeholder ? placeholder : defaultPlaceholderText;
  var iconSize = sizeConfig.icon;
  var viewports = useMemo(function () {
    var viewports = {
      s: false
    };
    if (dropdownOnBreakpoint) viewports[dropdownOnBreakpoint] = true;
    return viewports;
  }, [dropdownOnBreakpoint]);
  var isDropdownBreakpoint = useBreakpoint(viewports);
  var getPosition = useCallback(function (reference) {
    return reference && reference.current ? JSON.parse(JSON.stringify(reference.current.getBoundingClientRect())) : null;
  }, []);
  var isInside = useCallback(function (reference, x, y) {
    var position = getPosition(reference);
    return position && x >= position.left && x <= position.left + position.width && y >= position.top && y <= position.top + position.height;
  }, [getPosition]);
  var onSelectBlur = useCallback(function (event) {
    setIsFocused(false);
    if (typeof onBlur === 'function') onBlur(event);
  }, [onBlur]);
  var onSelectFocus = useCallback(function (event) {
    setIsFocused(true);
    if (typeof onFocus === 'function') onFocus(event);
  }, [onFocus]);
  useEffect(function () {
    var onKeyUp = function onKeyUp(event) {
      return event.keyCode === 27 && isFocused && onSelectBlur(event);
    }; // escape
    var listener = throttle(onKeyUp, keyThrottleMs);
    document.addEventListener('keyup', listener);
    return function () {
      return document.removeEventListener('keyup', listener);
    };
  }, [isFocused, onSelectBlur]);
  useEffect(function () {
    var onDocumentClick = function onDocumentClick(event) {
      if (!isDropdownBreakpoint || !isFocused) return false;
      var pageX = event.pageX,
        pageY = event.pageY;
      var x = pageX - window.scrollX;
      var y = pageY - window.scrollY;
      var isInsideSelect = isInside(ref, x, y);
      var isInsideOptions = listRef && listRef.current ? isInside(listRef, x, y) : false;
      if (!isInsideOptions && !isInsideSelect) onSelectBlur(event);
    };
    document.addEventListener('click', onDocumentClick);
    return function () {
      return document.removeEventListener('click', onDocumentClick);
    };
  }, [isDropdownBreakpoint, isFocused, isInside, ref, listRef, onSelectBlur]);
  var onClick = useCallback(function (event) {
    return isFocused ? onSelectBlur(event) : onSelectFocus(event);
  }, [isFocused, onSelectBlur, onSelectFocus]);
  var newClassname = useMemo(function () {
    var result = className;
    var sizeClass = "" + INPUT_SIZE_PREFIX + size;
    var kindClass = "" + INPUT_KIND_PREFIX + INPUT_KIND[kind];
    result = appendHtmlClass(result, 'select');
    result = appendHtmlClass(result, sizeClass);
    result = appendHtmlClass(result, kindClass);
    if (hasError) result = appendHtmlClass(result, 'error');
    if (isDisabled) result = appendHtmlClass(result, 'disabled');
    if (isLoading) result = appendHtmlClass(result, 'loading');
    if (showPlaceholder) result = appendHtmlClass(result, 'placeholder');
    if (isFocused) result = appendHtmlClass(result, 'focus');
    return result;
  }, [size, kind, className, hasError, isDisabled, isLoading, showPlaceholder, isFocused]);
  var getIcon = useCallback(function (name, size, className, spin, props) {
    if (className === void 0) {
      className = null;
    }
    if (spin === void 0) {
      spin = false;
    }
    if (props === void 0) {
      props = {};
    }
    return /*#__PURE__*/React.createElement(FlexItem, {
      className: className,
      span: "shrink"
    }, /*#__PURE__*/React.createElement(Icon, Object.assign({
      name: name,
      size: size,
      display: "iblock",
      spin: spin
    }, props)));
  }, []);
  var onSelect = useCallback(function (id, event) {
    if (typeof onChange === 'function') onChange(id, event);
    debounce(function () {
      return onSelectBlur(event);
    }, selectDelayMs)();
  }, [onChange, onSelectBlur]);
  var onAnimationStart = useCallback(function () {
    return setIsAnimating(true);
  }, [setIsAnimating]);
  var onAnimationEnd = useCallback(function () {
    return setIsAnimating(false);
  }, [setIsAnimating]);
  var getOptionListComponent = useCallback(function (_ref2) {
    var className = _ref2.className,
      rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
    if (!className) className = '';
    className = appendHtmlClass(className, 'select-options');
    var animate = isFocused ? 'visible' : 'hidden';
    return /*#__PURE__*/React.createElement(OptionList, Object.assign({
      className: className,
      innerRef: listRef,
      options: options,
      value: value,
      name: name,
      animate: animate,
      isRequired: isRequired,
      onSelect: onSelect,
      onAnimationStart: onAnimationStart,
      onAnimationEnd: onAnimationEnd
    }, rest));
  }, [isFocused, options, listRef, onAnimationStart, onAnimationEnd, onSelect, value, name, isRequired]);
  var dropdownStyle = useMemo(function () {
    var top = 0,
      left = 0,
      width = 0,
      maxHeight = '50vh';
    var bottomMargin = getSpaceValue(getConfig('SELECT_OPTION_LIST_BOTTOM_MARGIN'), 'px');
    var position = getPosition(ref);
    if (position) {
      top = position.top + position.height;
      left = position.left;
      width = position.width;
      maxHeight = window.scrollY + window.innerHeight - bottomMargin - top + "px";
    }
    var style = {
      top: top + "px",
      left: left + "px",
      width: width + "px",
      maxHeight: maxHeight
    };
    var display = !isFocused && !isAnimating ? {
      display: 'none'
    } : {};
    return _objectSpread(_objectSpread({}, style), display);
  }, [getPosition, ref, isFocused, isAnimating]);
  var optionListCorner = useMemo(function () {
    var cornerValue = getConfig('SELECT_OPTION_LIST_CORNER');
    return [0, 0, cornerValue, cornerValue];
  }, []);
  var getDropdownOptionList = useCallback(function () {
    var shadow = getConfig('SELECT_OPTION_LIST_SHADOW');
    var optionList = getOptionListComponent({
      style: dropdownStyle,
      corner: optionListCorner,
      shadow: shadow,
      canUseKeyboard: isFocused
    });
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/ReactDOM.createPortal(optionList, getPortalTarget()));
  }, [getOptionListComponent, dropdownStyle, optionListCorner, isFocused]);
  var getDrawerOptionList = useCallback(function () {
    var title = placeholderText;
    var size = getConfig('SELECT_DRAWER_SIZE');
    var optionList = getOptionListComponent({
      className: 'static',
      isAnimated: false
    });
    return /*#__PURE__*/React.createElement(Drawer, {
      size: size,
      isOpen: isFocused,
      position: "bottom",
      onClose: onSelectBlur,
      title: title
    }, optionList);
  }, [placeholderText, getOptionListComponent, isFocused, onSelectBlur]);
  var iconAnimationProps = useMemo(function () {
    var up = {
      rotateZ: '180deg'
    };
    var down = {
      rotateZ: '0deg'
    };
    var variants = {
      up: up,
      down: down
    };
    var animate = isFocused ? 'up' : 'down';
    var transition = {
      duration: getAnimationDuration(getConfig('OPTION_LIST_ITEM_ANIMATION_DURATION')),
      ease: getAnimationEasing('ease')
    };
    return {
      isAnimated: true,
      animate: animate,
      variants: variants,
      transition: transition,
      initial: 'down'
    };
  }, [isFocused]);
  var optionListComponent = useMemo(function () {
    return !isServer() && isDropdownBreakpoint ? getDropdownOptionList() : getDrawerOptionList();
  }, [isDropdownBreakpoint, getDrawerOptionList, getDropdownOptionList]);
  var leftIcon = useMemo(function () {
    return iconLeft && getIcon(iconLeft, iconSize, 'icon-left');
  }, [iconLeft, iconSize, getIcon]);
  var loadingIcon = useMemo(function () {
    return isLoading && getIcon(ICON_SPINNER, iconSize, 'icon-loading', true);
  }, [isLoading, iconSize, getIcon]);
  var rightIcon = useMemo(function () {
    return getIcon(ICON_DROPDOWN, iconSize, 'icon-right', false, iconAnimationProps);
  }, [getIcon, iconSize, iconAnimationProps]);
  var label = useMemo(function () {
    var option = options.find(function (i) {
      return i.id === value;
    });
    return option ? option.label : '';
  }, [options, value]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, Object.assign({
    className: newClassname,
    type: "raw",
    onClick: onClick
  }, rest), /*#__PURE__*/React.createElement(Flex, {
    alignItemsY: "center",
    innerRef: ref
  }, leftIcon, /*#__PURE__*/React.createElement(FlexItem, {
    span: "auto",
    alignY: "stretch"
  }, /*#__PURE__*/React.createElement(Element, {
    as: "div",
    className: "input-target"
  }, showPlaceholder ? placeholderText : label), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    value: value,
    name: name
  })), loadingIcon, rightIcon)), optionListComponent);
});
SelectMemo.defaultProps = {
  isDisabled: false,
  hasError: false,
  size: INPUT_SIZE_DEFAULT,
  kind: INPUT_KIND_DEFAULT,
  dropdownOnBreakpoint: 'l',
  isLoading: false,
  isRequired: false
};
export default withValidation(SelectMemo);